import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';
import KycUploadForm from '../components/KycUploadForm';
import KycDocumentsList from '../components/KycDocumentsList';
import { useNavigate } from 'react-router-dom';
import KycStatusBadge from '../components/KycStatusBadge';
import {
  Box, Typography, Tabs, Tab, Avatar, Grid, Paper, Button, Divider,
  IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, BottomNavigation, BottomNavigationAction, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, CircularProgress
} from '@mui/material';
import AccountStatusNotice from '../components/AccountStatusNotice';
import AccountStatusBanner from '../components/AccountStatusBanner';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CloseIcon from '@mui/icons-material/Close';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useTheme } from '@mui/material/styles';
import FlipCard from '../components/FlipCard';
import '../components/FlipCard.css';

// Placeholder widgets
function TransactionHistoryWidget() {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ color: '#3b1e6d', fontWeight: 600 }}>Transaction history</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>Last five transactions</Typography>
      <Divider sx={{ mb: 1 }} />
      <Typography color="text.secondary" align="center">No Transaction Yet</Typography>
    </Box>
  );
}
function CardWidget() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height={120}>
      <Paper elevation={3} sx={{ bgcolor: '#3b1e6d', color: 'white', width: 260, height: 120, borderRadius: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6">Safenet Bank</Typography>
        <Box mt={2} sx={{ fontSize: 24, letterSpacing: 4 }}>**** **** **** ****</Box>
      </Paper>
    </Box>
  );
}
function CryptoWalletWidget() {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ color: '#3b1e6d', fontWeight: 600 }}>Crypto wallet</Typography>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={6}><Typography>BTC</Typography><Typography>$0</Typography></Grid>
        <Grid item xs={6}><Typography>ETH</Typography><Typography>$0</Typography></Grid>
        <Grid item xs={6}><Typography>USDT</Typography><Typography>$0</Typography></Grid>
        <Grid item xs={6}><Typography>DOGE</Typography><Typography>$0</Typography></Grid>
      </Grid>
    </Box>
  );
}

const navTabs = [
  { label: 'Overview', icon: <DashboardIcon /> },
  { label: 'Transactions', icon: <ListAltIcon /> },
  { label: 'Cards', icon: <CreditCardIcon /> },
  { label: 'Loans', icon: <AccountBalanceWalletIcon /> },
  { label: 'Transfer', icon: <TrendingUpIcon /> },
  { label: 'KYC Verification', icon: <VerifiedUserIcon /> },
  { label: 'Account Settings', icon: <SettingsIcon /> }
];

export default function UserDashboardPage() {
  // ...hooks...
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // ...other useState hooks...
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [cards, setCards] = useState({ debit: [], credit: [] });
  const [loan, setLoan] = useState(null);
  const [crypto, setCrypto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kycData, setKycData] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [transactionTab, setTransactionTab] = useState(0);
  const [depositOpen, setDepositOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({ account: '', amount: '', accname: '', bankname: '', benefaddr: '', benefname: '', ifsc: '', rtn: '', remarks: '', acctype: 'personal' });
  const [cardRequestMsg, setCardRequestMsg] = useState('');
  const [transferHistory, setTransferHistory] = useState([]);
  const [loanForm, setLoanForm] = useState({ principal: '' });
  const [loanLoading, setLoanLoading] = useState(false);
  const [loanHistory, setLoanHistory] = useState([]);
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  // Restore bottomNavTabs for bottom navigation bar
  const bottomNavTabs = [
    { label: 'Overview', icon: <DashboardIcon /> },
    { label: 'Transactions', icon: <ListAltIcon /> },
    { label: 'KYC', icon: <VerifiedUserIcon /> },
    { label: 'Account', icon: <AccountCircleIcon /> },
    { label: 'Settings', icon: <SettingsIcon /> }
  ];

  // Mapping logic goes here, after dashboard is declared:
  const statusMap = {
    D: 'dormant',
    C: 'closed',
    DIS: 'disabled',
    S: 'suspicious',
    F: 'frozen',
    IRS: 'irs',
  };
  const rawStatus = dashboard?.status || dashboard?.account_status;
  const mappedStatus = statusMap[rawStatus] || rawStatus;
  console.log("Raw Status:", rawStatus, "Mapped Status:", mappedStatus);

  const handleBottomNavChange = (event, bottomNavIndex) => {
    let targetNavTabIndex;
    switch (bottomNavIndex) {
      case 0: // Overview in bottomNavTabs
        targetNavTabIndex = 0; // Overview in navTabs
        break;
      case 1: // Transactions in bottomNavTabs
        targetNavTabIndex = 1; // Transactions in navTabs
        break;
      case 2: // KYC in bottomNavTabs
        targetNavTabIndex = 5; // KYC Verification in navTabs
        break;
      case 3: // Account in bottomNavTabs
        targetNavTabIndex = 6; // Account Settings in navTabs
        break;
      case 4: // Settings in bottomNavTabs
        targetNavTabIndex = 6; // Account Settings in navTabs
        break;
      default:
        targetNavTabIndex = 0; // Default to Overview
    }
    setTab(targetNavTabIndex);
  };

  const getActiveBottomNavIndex = () => {
    if (tab === 0) return 0; // Overview tab -> Overview button (idx 0)
    if (tab === 1) return 1; // Transactions tab -> Transactions button (idx 1)
    if (tab === 5) return 2; // KYC Verification tab -> KYC button (idx 2)
    if (tab === 6) return 3; // Account Settings tab -> Account button (idx 3)
    return -1; // No specific button active for other tabs (e.g., Cards, Loans, Transfer)
  };

  // Logout handler
  function handleLogout() {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }

  // Fetch all dashboard data on mount and after loan actions
  const fetchDashboardData = async (token) => {
    console.log('Entering fetchDashboardData function...');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('https://api.safenetbanking.com/api/user/user_dashboard', {
        headers: { Authorization: 'Bearer ' + token }
      });
      console.log('API response data for dashboard:', res.data);
      setDashboard(res.data);
      setLoading(false);
    } catch (e) {
      console.log('Dashboard fetch error:', e);
      setDashboard(null);
      setLoading(false);
    }
  };

  // Fetch recent transactions for the overview tab
  const fetchRecentTransactions = async (token) => {
    try {
      // Fetch transfers directly - we know this endpoint works
      const transfersRes = await axios.get('https://api.safenetbanking.com/api/user/transfers', {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      // Format the transfer data for display
      const formattedTransactions = transfersRes.data.map(tx => ({
        id: tx.id,
        type: 'transfer',
        amount: tx.amount,
        description: `Transfer to ${tx.benefname || tx.account || 'recipient'}`,
        created_at: tx.created_at,
        status: tx.status || 'completed'
      }));
      
      // Sort by date (newest first) and take the most recent 5
      const sortedTransactions = formattedTransactions.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ).slice(0, 5);
      
      console.log('Recent transactions loaded:', sortedTransactions);
      setTransactions(sortedTransactions);
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      setTransactions([]);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDashboardData(token);
    fetchRecentTransactions(token);
    fetchKycStatus(token);
  }, [navigate]);
  
  // Fetch KYC status and documents
  const fetchKycStatus = async (token) => {
    setKycLoading(true);
    try {
      const response = await axios.get('https://api.safenetbanking.com/api/kyc/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('API response data for KYC:', response.data);
      setKycData(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data', err);
      console.log('Error response from API:', err.response?.data);
      // Potentially navigate to login if unauthorized or token expired
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        // navigate('/login');
      }
    } finally {
      setKycLoading(false);
    }
  };

  // Fetch user cards on mount and when a card is issued
  useEffect(() => {
    async function fetchCards() {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get('https://api.safenetbanking.com/api/user/cards', {
          headers: { Authorization: 'Bearer ' + token }
        });
        // Group cards by type for display
        const debit = res.data.filter(c => c.type === 'visa');
        const credit = res.data.filter(c => c.type === 'mastercard');
        setCards({ debit, credit });
      } catch {
        setCards({ debit: [], credit: [] });
      }
    }
    fetchCards();
  }, []);

  // Fetch user loans on mount and after submit
  const fetchLoans = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get('https://api.safenetbanking.com/api/user/loans', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setLoanHistory(res.data);
    } catch {
      setLoanHistory([]);
    }
  };
  useEffect(() => { fetchLoans(); }, []);

  // Transaction tab names and endpoints
  const txTabNames = ['deposit', 'withdrawal', 'loan', 'investment'];
  const txEndpoints = {
    deposit: 'get_transactions?type=deposit',
    withdrawal: 'get_transactions?type=withdrawal',
    loan: 'loans',
    investment: 'get_transactions?type=investment'
  };
  // Fetch transactions by type when tab changes
  useEffect(() => {
    if (tab === 1) {
      const token = localStorage.getItem('token');
      const endpoint = txEndpoints[txTabNames[transactionTab]];
      axios.get(`https://api.safenetbanking.com/api/user/${endpoint}`,
        { headers: { Authorization: 'Bearer ' + token } }
      )
        .then(res => setTransactions(res.data))
        .catch(() => setTransactions([]));
    }
  }, [tab, transactionTab]);

  // Fetch transfer history when Transfer tab is active
  useEffect(() => {
    if (tab === 4) {
      const token = localStorage.getItem('token');
      axios.get('https://api.safenetbanking.com/api/user/transfers', {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(res => setTransferHistory(res.data));
    }
  }, [tab]);

  useEffect(() => {
    console.log('Dashboard state updated:', dashboard);
  }, [dashboard]);

  // Deposit Modal Options
  const DepositDialog = (
    <Dialog open={depositOpen} onClose={() => setDepositOpen(false)}>
      <DialogTitle>Choose Deposit Method</DialogTitle>
      <DialogContent>
        <Box my={2}>
          <Button fullWidth sx={{ mb: 2 }} variant="outlined" onClick={() => setSupportOpen(true)}>PayPal (Manual)</Button>
          <Button fullWidth sx={{ mb: 2 }} variant="outlined" onClick={() => setSupportOpen(true)}>Crypto (Manual)</Button>
          <Button fullWidth variant="outlined" onClick={() => setSupportOpen(true)}>Bank Transfer (Manual)</Button>
        </Box>
        <Typography variant="body2" color="text.secondary">Follow the on-screen instructions after selecting a method. Your deposit will be verified manually by support.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDepositOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Transfer Funds Modal
  const TransferDialog = (
    <Dialog open={transferOpen} onClose={() => setTransferOpen(false)}>
      <DialogTitle>Transfer Funds</DialogTitle>
      <DialogContent>
        <TextField label="Recipient Account Number" fullWidth sx={{ my: 1 }} value={transferData.account} onChange={e => setTransferData({ ...transferData, account: e.target.value })} />
        <TextField label="Amount ($)" fullWidth sx={{ my: 1 }} type="number" value={transferData.amount} onChange={e => setTransferData({ ...transferData, amount: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
        <Button variant="contained" disabled>Send (Demo Only)</Button>
      </DialogActions>
    </Dialog>
  );

  // Request Card logic
  function handleRequestCard(type) {
    if ((dashboard?.balance ?? 0) < 25) {
      setCardRequestMsg('You must have at least $25 balance to request a card.');
      return;
    }
    setCardDialogType(type);
    setCardDialogOpen(true);
  }
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [cardDialogType, setCardDialogType] = useState('visa');
  const [cardSubmitting, setCardSubmitting] = useState(false);
  async function submitCardApplication() {
    setCardSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://api.safenetbanking.com/api/user/card_application', {
        type: cardDialogType
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setCardRequestMsg('Card application submitted! Await admin approval.');
    } catch {
      setCardRequestMsg('Failed to submit card application.');
    }
    setCardSubmitting(false);
    setCardDialogOpen(false);
  }
  const CardRequestDialog = (
    <Dialog open={cardDialogOpen} onClose={() => setCardDialogOpen(false)}>
      <DialogTitle>Request Card</DialogTitle>
      <DialogContent>
        <Typography>Select card type:</Typography>
        <Box my={2}>
          <Button variant={cardDialogType === 'visa' ? 'contained' : 'outlined'} sx={{ mr: 2 }} onClick={() => setCardDialogType('visa')}>Visa</Button>
          <Button variant={cardDialogType === 'mastercard' ? 'contained' : 'outlined'} onClick={() => setCardDialogType('mastercard')}>MasterCard</Button>
        </Box>
        <Typography>Your account will be charged $25 if approved.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCardDialogOpen(false)}>Cancel</Button>
        <Button onClick={submitCardApplication} disabled={cardSubmitting} variant="contained">Submit</Button>
      </DialogActions>
    </Dialog>
  );

  // --- OTP Transfer Modal State ---
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [transferStep, setTransferStep] = useState('form'); // 'form' | 'otp' | 'success'
  const [transferMsg, setTransferMsg] = useState('');

  // --- Updated Transfer Submit Handler ---
  async function handleTransferSubmit() {
    // Basic validation
    if (!transferData.amount || !transferData.account || !transferData.accname || !transferData.bankname || !transferData.benefaddr || !transferData.benefname || !transferData.ifsc || !transferData.rtn || !transferData.remarks || !transferData.acctype) {
      alert('Please fill all required fields.');
      return;
    }
    
    // Check account status
    if (dashboard?.status && dashboard.status !== 'active') {
      alert(`Unable to process transfer. Your account status is currently: ${dashboard.status}. Please contact customer support.`);
      return;
    }
    
    // Check if user has sufficient funds
    const transferAmount = parseFloat(transferData.amount);
    const currentBalance = parseFloat(dashboard?.balance || 0);
    
    if (isNaN(transferAmount) || transferAmount <= 0) {
      alert('Please enter a valid transfer amount.');
      return;
    }
    
    if (currentBalance < transferAmount) {
      alert('Insufficient funds. Your current balance is $' + currentBalance.toFixed(2) + '.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      setTransferMsg('');
      // Step 1: Initiate transfer (send OTP)
      const res = await axios.post('https://api.safenetbanking.com/api/user/initiate_transfer', transferData, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        setOtpDialogOpen(true);
        setTransferStep('otp');
        setTransferMsg('OTP sent to your email.');
      } else if (res.data.onscreen_otp) {
        setOtpDialogOpen(true);
        setTransferStep('otp');
        setTransferMsg('OTP email failed. Use this OTP: ' + res.data.onscreen_otp);
      } else {
        setTransferMsg(res.data.error || 'Failed to initiate transfer.');
      }
    } catch (e) {
      setTransferMsg(e.response?.data?.error || 'Failed to initiate transfer.');
    }
  }

  // --- OTP Verification Handler ---
  async function handleOtpSubmit() {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('https://api.safenetbanking.com/api/user/verify_transfer_otp', {
        ...transferData,
        otp: otpValue
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        setTransferStep('success');
        setTransferMsg('Transfer submitted for admin approval.');
        setOtpDialogOpen(false);
        afterTransfer();
      } else {
        setTransferMsg(res.data.error || 'OTP verification failed.');
      }
    } catch (e) {
      setTransferMsg(e.response?.data?.error || 'OTP verification failed.');
    }
  }

  // --- OTP Dialog Modal ---
  const OtpDialog = (
    <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)}>
      <DialogTitle>Enter Transfer OTP</DialogTitle>
      <DialogContent>
        <Typography mb={2}>Please enter the OTP sent to your registered email to confirm this transfer.</Typography>
        {transferMsg && transferMsg.includes('Use this OTP:') && (
          <Box mb={2} p={2} bgcolor="#fff3cd" borderRadius={2} border="1px solid #ffeeba">
            <Typography color="warning.main" fontWeight="bold">{transferMsg}</Typography>
          </Box>
        )}
        <TextField label="OTP" fullWidth value={otpValue} onChange={e => setOtpValue(e.target.value)} autoFocus />
        {transferMsg && !transferMsg.includes('Use this OTP:') && <Typography color="error" mt={2}>{transferMsg}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOtpDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleOtpSubmit}>Verify & Confirm</Button>
      </DialogActions>
    </Dialog>
  );

  // --- Transfer Success Message ---
  const TransferSuccessDialog = (
    <Dialog open={transferStep === 'success'} onClose={() => setTransferStep('form')}>
      <DialogTitle>Transfer Submitted</DialogTitle>
      <DialogContent>
        <Typography>Transfer submitted for admin approval. You will be notified once processed.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTransferStep('form')}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  async function afterTransfer() {
    setTransferData({ account: '', amount: '', accname: '', bankname: '', benefaddr: '', benefname: '', ifsc: '', rtn: '', remarks: '', acctype: 'personal' });
    const token = localStorage.getItem('token');
    
    // Refresh transfer history
    axios.get('https://api.safenetbanking.com/api/user/transfers', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(res => setTransferHistory(res.data));
    
    // Refresh dashboard data to show updated balance
    fetchDashboard();
    
    // Refresh recent transactions for the overview tab
    fetchRecentTransactions();
  };

  async function handleLoanSubmit() {
    if (!loanForm.principal || isNaN(Number(loanForm.principal)) || Number(loanForm.principal) <= 0) {
      alert('Please enter a valid principal amount (greater than 0).');
      return;
    }
    setLoanLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Submitting principal:', loanForm.principal);
      const res = await axios.post('https://api.safenetbanking.com/api/user/loans', { principal: loanForm.principal }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        alert('Loan application submitted!');
        setLoanForm({ principal: '' });
        fetchLoans(); // Refresh loan list
        fetchDashboard(); // Refresh dashboard (loan amount)
      } else {
        alert(res.data.error || 'Failed to apply for loan.');
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to apply for loan.');
    }
    setLoanLoading(false);
  }

  // Profile and Settings Dialogs (simple modal implementation)
  const ProfileDialog = (
    <Dialog open={showProfile} onClose={() => setShowProfile(false)}>
      <DialogTitle>Profile</DialogTitle>
      <DialogContent>
        <Typography>Name: {dashboard?.full_name || ''}</Typography>
        <Typography>Email: {dashboard?.email || '---'}</Typography>
        <Typography>Phone: {dashboard?.phone || '---'}</Typography>
        <Typography>Account #: {dashboard?.account_number || ''}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowProfile(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
  const SettingsDialog = (
    <Dialog open={showSettings} onClose={() => setShowSettings(false)}>
      <DialogTitle>Account Settings</DialogTitle>
      <DialogContent>
        <Typography>Password change and notification settings can go here.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowSettings(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Support Modal for all payment methods
  function SupportModal({ open, onClose }) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Please contact support to continue with this transaction.</Typography>
          <Box display="flex" justifyContent="center">
            <img src="/support.13b798d6ec4d62e3e4d2.jpg" alt="Support" style={{ maxWidth: 260, borderRadius: 8 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box sx={{ background: '#f7f6f2', minHeight: '100vh', pb: isMobile ? 7 : 0 }}>
      {/* Hero/Header Area */}
      <Box sx={{ background: 'linear-gradient(120deg, #3b1e6d 0%, #5f2c82 100%)', color: 'white', pb: 4, px: 0, position: 'relative' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 3, px: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {isMobile && (
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'white', mr: 1 }}>
                <MenuIcon fontSize="large" />
              </IconButton>
            )}
            <Box component="img" src="/logo.jpg" alt="Safenet Bank Logo" sx={{ width: 56, height: 56, borderRadius: '4px' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" letterSpacing={1}>Safenet Bank</Typography>
            </Box>
            <Box flexGrow={1} />
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={2}>
                <IconButton onClick={() => setShowProfile(true)} sx={{ color: 'white' }}><AccountCircleIcon fontSize="large" /></IconButton>
                <IconButton onClick={() => setShowSettings(true)} sx={{ color: 'white' }}><SettingsIcon fontSize="large" /></IconButton>
                <IconButton onClick={handleLogout} sx={{ color: 'white' }}><PowerSettingsNewIcon fontSize="large" /></IconButton>
              </Box>
            )}
          </Box>
          <Typography variant="h5" fontWeight="bold" mt={4} mb={0.5}>
            {dashboard?.full_name ? `Welcome ${dashboard.full_name}!` : 'Welcome!'}
          </Typography>
        </Box>
        {/* Tabs below hero (desktop only) */}
        {!isMobile && (
          <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 0,
                '& .MuiTab-root': { color: 'white', fontWeight: 600, fontSize: '1.1rem', minHeight: 0, px: 3 },
                '& .Mui-selected': { borderBottom: '2px solid #fff' }
              }}
            >
              {navTabs.map((item, idx) => (
                <Tab key={item.label} label={item.label} icon={item.icon} iconPosition="start" value={idx} />
              ))}
            </Tabs>
          </Box>
        )}
      </Box>

      {/* Drawer for mobile navigation */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280 }}>
          <Box sx={{ background: 'linear-gradient(120deg, #3b1e6d 0%, #5f2c82 100%)', color: 'white', p: 3, textAlign: 'center' }}>
            <Box component="img" src="/logo.jpg" alt="Safenet Bank Logo" sx={{ width: 56, height: 56, borderRadius: '4px', mb: 1, mx: 'auto' }} />
            <Typography variant="subtitle1" fontWeight={700}>{dashboard?.full_name || ''}</Typography>
            <Typography variant="body2">{dashboard?.account_number || ''}</Typography>
          </Box>
          <List>
            {navTabs.map((item, idx) => (
              <ListItem button key={item.label} selected={tab === idx} onClick={() => { setTab(idx); setDrawerOpen(false); }}>
                <ListItemIcon sx={{ color: '#3b1e6d' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
            <ListItem button>
              <ListItemIcon sx={{ color: '#3b1e6d' }}><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon sx={{ color: '#e53935' }}><ExitToAppIcon /></ListItemIcon>
              <ListItemText primary={<span style={{ color: '#e53935' }}>Sign out</span>} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Tab Panels - widgets in grid */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: isMobile ? 2 : -5, px: 2 }}>
        {/* Account Status Notice - removed to avoid duplicate banner */}
        {/* (Removed AccountStatusNotice for cleaner UI) */}
        
        {tab === 0 && (
          <>
            {console.log('Current Tab:', tab, 'Raw Status:', rawStatus, 'Mapped Status:', mappedStatus)}
            {(rawStatus && mappedStatus !== 'active') && (
              <AccountStatusBanner status={mappedStatus} />
            )}
            <Grid container spacing={3}>
              {/* Balance */}
              <Grid item xs={12} md={4}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 4, bgcolor: '#2d1865', color: 'white', minHeight: 160 }}>
                  <Typography variant="subtitle1">Total balance</Typography>
                  <Typography variant="body2">Account Number {dashboard?.account_number || ''}</Typography>
                  <Typography variant="h4" fontWeight="bold" mt={2}>{formatCurrency(dashboard?.balance)}</Typography>
                  <Box mt={2} display="flex" gap={2}>
                    <Button 
                      variant="contained" 
                      sx={{ background: 'white', color: '#3b1e6d', borderRadius: 3, fontWeight: 600 }} 
                      onClick={() => setDepositOpen(true)}
                    >
                      Deposit
                    </Button>
                    <Button 
                      variant="contained" 
                      sx={{ background: 'white', color: '#3b1e6d', borderRadius: 3, fontWeight: 600 }} 
                      onClick={() => dashboard?.account_status === 'active' ? setTab(4) : alert(`Sorry, your account is ${dashboard?.account_status}/inactive, please contact customer care info@safenetbanking.com`)}
                      disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
                    >
                      Send Money
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              {/* Loan + Investment */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 4, minHeight: 80 }}>
                      <Typography variant="subtitle1">Loan</Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>{formatCurrency(dashboard?.loan)}</Typography>
                      <Button variant="outlined" sx={{ mt: 2, color: '#3b1e6d', borderColor: '#3b1e6d', borderRadius: 3, fontWeight: 600 }}>Repay Loan</Button>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 4, minHeight: 80 }}>
                      <Typography variant="subtitle1">Investment</Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>{formatCurrency(dashboard?.investment)}</Typography>
                      <Button variant="outlined" sx={{ mt: 2, color: '#3b1e6d', borderColor: '#3b1e6d', borderRadius: 3, fontWeight: 600 }}>View Investment</Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            {/* Transaction history, Cards, Crypto wallet */}
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 4, minHeight: 170 }}>
                <Typography variant="subtitle1" sx={{ color: '#3b1e6d', fontWeight: 600 }}>Transaction history</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>Last five transactions</Typography>
                <Divider sx={{ mb: 1 }} />
                {transactions.length === 0 ? (
                  <Typography color="text.secondary" align="center">No Transaction Yet</Typography>
                ) : (
                  <Box>
                    {transactions.map(tx => (
                      <Box key={tx.id} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="500">
                            {tx.type === 'transfer' ? 'Transfer' : 
                             tx.type === 'deposit' ? 'Deposit' : 
                             tx.type === 'withdrawal' ? 'Withdrawal' : 
                             tx.action || 'Transaction'}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color={tx.type === 'deposit' ? 'success.main' : 'error.main'}>
                            {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(tx.amount, 2, '')}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {tx.description || ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 4, minHeight: 170 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" sx={{ color: '#3b1e6d', fontWeight: 600 }}>Cards</Typography>
                  <Button variant="text" sx={{ color: '#6c63ff', fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} onClick={() => handleRequestCard('debit')}>Request Card +</Button>
                </Box>
                {cards.debit.length === 0 && cards.credit.length === 0 ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Typography color="text.secondary" align="center">No Cards Yet</Typography>
                    <Box mt={2}>
                      <FlipCard card={{
                        card_number: '3425830490109048',
                        full_name: dashboard?.full_name || 'CARD HOLDER',
                        expiry: '10/27',
                        cvc: '420'
                      }} />
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {cards.debit.map(card => (
                      <Paper key={card.id} sx={{ bgcolor: '#3b1e6d', color: 'white', my: 1, p: 2, borderRadius: 3 }}>
                        <Typography variant="body2">Debit Card</Typography>
                        <Typography variant="h6">**** **** **** {card.card_number.slice(-4)}</Typography>
                        <Typography variant="caption">{card.status}</Typography>
                      </Paper>
                    ))}
                    {cards.credit.map(card => (
                      <Paper key={card.id} sx={{ bgcolor: '#3b1e6d', color: 'white', my: 1, p: 2, borderRadius: 3 }}>
                        <Typography variant="body2">Credit Card</Typography>
                        <Typography variant="h6">**** **** **** {card.card_number.slice(-4)}</Typography>
                        <Typography variant="caption">{card.status}</Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 4, minHeight: 170 }}>
                <Typography variant="subtitle1" sx={{ color: '#3b1e6d', fontWeight: 600 }}>Crypto wallet</Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={6}><Typography>BTC</Typography><Typography>{formatCurrency(crypto?.btc ?? 0)}</Typography></Grid>
                  <Grid item xs={6}><Typography>ETH</Typography><Typography>{formatCurrency(crypto?.eth ?? 0)}</Typography></Grid>
                  <Grid item xs={6}><Typography>USDT</Typography><Typography>{formatCurrency(crypto?.usdt ?? 0)}</Typography></Grid>
                  <Grid item xs={6}><Typography>DOGE</Typography><Typography>{formatCurrency(crypto?.doge ?? 0)}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </>
        )}
        {tab === 1 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>Transaction History</Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={transactionTab} onChange={(e, v) => setTransactionTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 0 }}>
                <Tab label="Deposits" sx={{ minWidth: 120, fontWeight: 500 }} />
                <Tab label="Withdrawals" sx={{ minWidth: 120, fontWeight: 500 }} />
                <Tab label="Loans" sx={{ minWidth: 120, fontWeight: 500 }} />
                <Tab label="Investments" sx={{ minWidth: 120, fontWeight: 500 }} />
              </Tabs>
            </Box>
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3, bgcolor: '#fafbff', maxWidth: 900, mx: 'auto' }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>{txTabNames[transactionTab].charAt(0).toUpperCase() + txTabNames[transactionTab].slice(1)}</Typography>
              {transactions.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ fontSize: 18 }}>No {txTabNames[transactionTab].charAt(0).toUpperCase() + txTabNames[transactionTab].slice(1)} Transaction Yet</Typography>
              ) : (
                <Box>
                  {transactions.map(tx => (
                    <Box key={tx.id} sx={{ mb: 2 }}>
                      <Typography variant="body2">{tx.description || tx.type} - {formatCurrency(tx.amount)}</Typography>
                      <Typography variant="caption" color="text.secondary">{tx.created_at}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        )}
        {tab === 2 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>Cards</Typography>
            <Grid container spacing={3} maxWidth={900} mx="auto">
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 2 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: 18 }}>Debit Cards</Typography>
                  {cards.debit.length === 0 ? (
                    <>
                      <Typography color="text.secondary">No Debit Cards</Typography>
                      <Box mt={2}>
                        <FlipCard card={{
                          card_number: '3425830490109048',
                          full_name: dashboard?.full_name || 'CARD HOLDER',
                          expiry: '10/27',
                          cvc: '420'
                        }} />
                      </Box>
                      <Button variant="contained" sx={{ mt: 2 }} onClick={() => handleRequestCard('debit')}>Request Debit Card</Button>
                    </>
                  ) : (
                    cards.debit.map(card => (
                      <Box key={card.id} sx={{ my: 1 }}>
                        <FlipCard card={card} />
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: 18 }}>Credit Cards</Typography>
                  {cards.credit.length === 0 ? (
                    <>
                      <Typography color="text.secondary">No Credit Cards</Typography>
                      <Box mt={2}>
                        <FlipCard card={{
                          card_number: '3425830490109048',
                          full_name: dashboard?.full_name || 'CARD HOLDER',
                          expiry: '10/27',
                          cvc: '420'
                        }} />
                      </Box>
                      <Button variant="contained" sx={{ mt: 2 }} onClick={() => handleRequestCard('credit')}>Request Credit Card</Button>
                    </>
                  ) : (
                    cards.credit.map(card => (
                      <Box key={card.id} sx={{ my: 1 }}>
                        <FlipCard card={card} />
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
        {tab === 3 && (
          <Box sx={{ mt: 6, mb: 6, maxWidth: 500, mx: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" mb={2}>Apply For Loan</Typography>
            <Box display="flex" gap={2} mb={3}>
              <Button variant="contained" sx={{ background: '#241b4a', fontWeight: 600 }} onClick={handleLoanSubmit} disabled={loanLoading}>Apply Loan</Button>
              <Button variant="outlined" color="inherit" onClick={() => setLoanForm({ principal: '' })}>Discard</Button>
            </Box>
            <TextField label="Principal Amount ($)" type="number" placeholder="Enter amount" fullWidth sx={{ mb: 2 }} value={loanForm.principal} onChange={e => setLoanForm({ ...loanForm, principal: e.target.value })} />
            {/* Loan History Table */}
            <Typography variant="h6" fontWeight="bold" mt={4} mb={2}>Loan Applications</Typography>
            {loanHistory.length === 0 ? (
              <Typography color="text.secondary">No loan applications yet.</Typography>
            ) : (
              <Box>
                {loanHistory.map(loan => (
                  <Paper key={loan.id} sx={{ p: 2, my: 1 }}>
                    <Typography variant="body2"><b>Principal:</b> ${loan.principal}</Typography>
                    <Typography variant="body2"><b>Status:</b> {loan.status}</Typography>
                    <Typography variant="caption" color="text.secondary">Submitted: {new Date(loan.created_at).toLocaleString()}</Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
        {tab === 4 && (
          <Box sx={{ mt: 6, mb: 6, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" mb={2}>Make Transfer</Typography>
            
            {/* Account Status Warning - only show for non-active accounts */}
            {dashboard?.account_status && dashboard.account_status !== 'active' ? (
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: '#fffbe5', 
                  color: '#856404',
                  border: '1px solid #ffeeba',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="body1">
                  Sorry, your account is {dashboard.account_status}/inactive, please contact customer care info@safenetbanking.com
                </Typography>
                <IconButton 
                  size="small" 
                  sx={{ color: '#856404' }}
                  aria-label="close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            ) : (
              <Typography mb={2}>Please, carefully fill required details below to transfer funds.</Typography>
            )}
            
            {/* Transfer Form - Always show but disable inputs for non-active accounts */}
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <TextField
                label="From"
                value={`${dashboard?.full_name?.toUpperCase() || ''} - ${dashboard?.account_number || ''} - [$${dashboard?.balance ?? 0}]`}
                fullWidth
                sx={{ mb: 2 }}
                disabled
              />
              <TextField 
                label="Amount ($)" 
                placeholder="eg. 30000" 
                fullWidth 
                sx={{ mb: 2 }} 
                type="number" 
                value={transferData.amount} 
                onChange={e => setTransferData({ ...transferData, amount: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Account Number" 
                placeholder="eg. 2453542540" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.account} 
                onChange={e => setTransferData({ ...transferData, account: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Account Name" 
                placeholder="eg. Johnson Peters" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.accname || ''} 
                onChange={e => setTransferData({ ...transferData, accname: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Bank Name" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.bankname || ''} 
                onChange={e => setTransferData({ ...transferData, bankname: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Beneficiary Address" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.benefaddr || ''} 
                onChange={e => setTransferData({ ...transferData, benefaddr: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Beneficiary Name" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.benefname || ''} 
                onChange={e => setTransferData({ ...transferData, benefname: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="IFSC/Swift Code" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.ifsc || ''} 
                onChange={e => setTransferData({ ...transferData, ifsc: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Routing Transit Number (RTN)" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.rtn || ''} 
                onChange={e => setTransferData({ ...transferData, rtn: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField 
                label="Remarks" 
                fullWidth 
                sx={{ mb: 2 }} 
                value={transferData.remarks || ''} 
                onChange={e => setTransferData({ ...transferData, remarks: e.target.value })} 
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              />
              <TextField
                select
                label="Account Type"
                fullWidth
                sx={{ mb: 2 }}
                value={transferData.acctype}
                onChange={e => setTransferData({ ...transferData, acctype: e.target.value })}
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              >
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="savings">Savings</MenuItem>
                <MenuItem value="checking">Checking</MenuItem>
              </TextField>
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ mt: 2, py: 1.5, fontWeight: 600 }} 
                onClick={handleTransferSubmit}
                disabled={dashboard?.account_status && dashboard.account_status !== 'active'}
              >
                Submit Transfer
              </Button>
            </Paper>
            
            {/* Always show transfer history regardless of account status */}
            {transferHistory.length > 0 && (
              <Box mt={4}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Recent Transfers</Typography>
                {transferHistory.map(tx => (
                  <Paper key={tx.id} sx={{ p: 2, my: 1 }}>
                    <Typography variant="body2">{tx.description}</Typography>
                    <Typography variant="caption">{tx.created_at} &mdash; ${tx.amount}</Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
        {tab === 5 && (
          <Box sx={{ mt: 6, mb: 6, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>KYC Verification</Typography>
            
            {kycLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h6">
                        Verification Level: {kycData?.kyc_level || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Status: <KycStatusBadge status={kycData?.kyc_status || 'not_submitted'} />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Alert 
                        severity={
                          kycData?.kyc_status === 'verified' ? 'success' : 
                          kycData?.kyc_status === 'rejected' ? 'error' : 
                          kycData?.kyc_status === 'pending' ? 'warning' : 'info'
                        }
                      >
                        {kycData?.kyc_status === 'verified' ? 'Your identity has been verified!' : 
                         kycData?.kyc_status === 'rejected' ? 'Your verification was rejected. Please submit new documents.' : 
                         kycData?.kyc_status === 'pending' ? 'Your verification is pending review.' : 
                         'Please complete KYC verification to unlock all account features.'}
                      </Alert>
                    </Grid>
                  </Grid>
                </Paper>
                
                <Typography variant="h6" gutterBottom>Upload Documents</Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  To verify your identity, please upload the following documents:
                  <ul>
                    <li>Government-issued ID (passport, driver's license, or national ID)</li>
                    <li>Proof of address (utility bill or bank statement less than 3 months old)</li>
                  </ul>
                </Typography>
                
                <KycUploadForm onUploadSuccess={fetchKycStatus} />
                
                {kycData?.documents && kycData.documents.length > 0 && (
                  <KycDocumentsList documents={kycData.documents} />
                )}
              </>
            )}
          </Box>
        )}
        {tab === 6 && (
          <Box sx={{ mt: 5, mb: 5, maxWidth: 700, mx: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>Account Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Full Name</Typography><Typography color="text.secondary">{dashboard?.full_name || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Email</Typography><Typography color="text.secondary">{dashboard?.email || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Phone</Typography><Typography color="text.secondary">{dashboard?.phone || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Account Number</Typography><Typography color="text.secondary">{dashboard?.account_number || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Account Type</Typography><Typography color="text.secondary">{dashboard?.account_type || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Account Status</Typography><Typography color="text.secondary">{dashboard?.account_status || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Balance</Typography><Typography color="text.secondary">${dashboard?.balance || '0.00'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Active Since</Typography><Typography color="text.secondary">{dashboard?.active_since || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Address</Typography><Typography color="text.secondary">{dashboard?.address || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Country</Typography><Typography color="text.secondary">{dashboard?.country || '--'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography fontWeight={500}>Region</Typography><Typography color="text.secondary">{dashboard?.region || '--'}</Typography></Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight="bold" mb={2}>Beneficiary</Typography>
            {(dashboard?.beneficiaries || []).length === 0 ? (
              <Typography color="text.secondary">No beneficiaries added.</Typography>
            ) : (
              dashboard.beneficiaries.map((b, i) => (
                <Box key={i} mb={2}>
                  <Typography fontWeight={500}>Name</Typography><Typography color="text.secondary">{b.name}</Typography>
                  <Typography fontWeight={500}>Address</Typography><Typography color="text.secondary">{b.address}</Typography>
                </Box>
              ))
            )}
            
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight="bold" mb={2}>ATM Card</Typography>
            {(cards.debit.length === 0 && cards.credit.length === 0) ? (
              <Typography color="text.secondary">No cards issued.</Typography>
            ) : (
              [...cards.debit, ...cards.credit].map((card, i) => (
                <Box key={i} mb={2}>
                  <Typography fontWeight={500}>Card Number</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography color="text.secondary">**** **** **** {card.card_number?.slice(-4) || '--'}</Typography>
                    <Button size="small" onClick={() => navigator.clipboard.writeText(card.card_number)}>Copy</Button>
                  </Box>
                  <Typography fontWeight={500}>Expiring Date</Typography><Typography color="text.secondary">{card.expiry || '--'}</Typography>
                  <Typography fontWeight={500}>CVV</Typography>
                  <span style={{ background: '#eee', padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }} title="Click to reveal" onClick={e => { e.target.innerText = card.cvv; setTimeout(() => { e.target.innerText = '***'; }, 2000); }}>***</span>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* Bottom Navigation for mobile */}
      {isMobile && (
        <BottomNavigation
          showLabels
          sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300, borderTop: '1px solid #eee', bgcolor: 'white' }}
          value={getActiveBottomNavIndex()}
          onChange={handleBottomNavChange}
        >
          {bottomNavTabs.map((item, idx) => (
            <BottomNavigationAction key={item.label} label={item.label} icon={item.icon} value={idx} />
          ))}
        </BottomNavigation>
      )}
      {DepositDialog}
      {TransferDialog}
      {CardRequestDialog}
      <Dialog open={!!cardRequestMsg} onClose={() => setCardRequestMsg('')}>
        <DialogTitle>Card Request</DialogTitle>
        <DialogContent>
          <Typography>{cardRequestMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardRequestMsg('')}>Close</Button>
        </DialogActions>
      </Dialog>
      {ProfileDialog}
      {SettingsDialog}
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
      {OtpDialog}
      {TransferSuccessDialog}
    </Box>
  );
}
